// ---------------------------------------------------------------------------
// Roary Phrase System
// Centralized copy for Roary-enhanced modals. Each phrase key maps to a set
// of title/message pairs with optional weights for controlling frequency.
// ---------------------------------------------------------------------------

export type RoaryPhraseKey =
  | 'published'
  | 'page_created'
  | 'component_added'
  | 'changes_saved'
  | 'generic_success'
  | 'generic_info'
  | 'blog_published';

export interface RoaryPhrase {
  title: string;
  message: string;
  /** Controls how often this phrase appears relative to others in the set.
   *  Defaults to 1. Use 0.2–0.3 for brand-forward/personality phrases
   *  so they feel like rare treats, not constant noise. */
  weight?: number;
}

export const ROARY_PHRASES: Record<RoaryPhraseKey, RoaryPhrase[]> = {
  // ----------------------------------------
  // MILESTONE: Site Published
  // ----------------------------------------
  published: [
    { title: "We're live.", message: 'Your website is out in the world.' },
    { title: 'Published.', message: 'Launch complete and ready for visitors.' },
    { title: 'Launch complete.', message: 'Your site is now publicly available.' },
    { title: 'Site is live.', message: 'Everything is up and running.' },
    { title: 'Live and ready.', message: 'Visitors can now see your updates.' },
    // Brand touch
    { title: 'Flames on.', message: 'Your site is live and breathing fire.', weight: 0.25 },
  ],

  // ----------------------------------------
  // MEDIUM FREQUENCY: Page Created
  // ----------------------------------------
  page_created: [
    { title: 'Page created.', message: 'Ready for content.' },
    { title: 'Fresh start.', message: 'You can begin building immediately.' },
    { title: 'All set.', message: 'The page is ready for editing.' },
    { title: 'Added.', message: 'Your new page is now part of the site.' },
    // Brand touch
    {
      title: 'New territory.',
      message: 'Another corner of your site, ready to grow.',
      weight: 0.25,
    },
  ],

  // ----------------------------------------
  // HIGH FREQUENCY: Component Added
  // Keep these ultra-short — users are mid-flow.
  // ----------------------------------------
  component_added: [
    { title: 'Added.', message: 'Component is in place.' },
    { title: 'Done.', message: 'Component added.' },
    { title: 'Placed.', message: 'You can edit it now.' },
    { title: 'All set.', message: 'Component is ready.' },
    // Brand touch
    { title: 'Stacked.', message: 'Your layout just got stronger.', weight: 0.2 },
  ],

  // ----------------------------------------
  // VERY HIGH FREQUENCY: Changes Saved
  // Shortest possible — should feel almost invisible.
  // ----------------------------------------
  changes_saved: [
    { title: 'Saved.', message: 'Your changes are secure.' },
    { title: 'Locked in.', message: 'Updates saved.' },
    { title: 'Done.', message: 'Everything is up to date.' },
    { title: 'All set.', message: 'Progress preserved.' },
    // Brand touch
    { title: 'Safe and sound.', message: 'Your work is safely stored.', weight: 0.2 },
  ],

  // ----------------------------------------
  // GENERIC SUCCESS (Fallback)
  // ----------------------------------------
  generic_success: [
    { title: 'Success.', message: 'Everything went smoothly.' },
    { title: 'Done.', message: "You're good to go." },
    { title: 'Nice work.', message: "That's been taken care of." },
    { title: 'Complete.', message: 'Everything is in place.' },
    // Brand touch
    { title: 'Strong move.', message: 'Momentum is building.', weight: 0.25 },
  ],

  // ----------------------------------------
  // GENERIC INFO
  // ----------------------------------------
  generic_info: [
    { title: 'Heads up.', message: "Here's the latest update." },
    { title: 'Quick note.', message: 'Just so you know.' },
    { title: 'FYI.', message: "Here's what just happened." },
    { title: 'Update.', message: 'Everything looks good.' },
    // Brand touch
    { title: 'All clear.', message: 'Nothing to worry about.', weight: 0.25 },
  ],

  // ----------------------------------------
  // BLOG POST PUBLISHED
  // ----------------------------------------
  blog_published: [
    { title: 'Post published.', message: 'Your article is now live.' },
    { title: 'Published.', message: 'Readers can now access your post.' },
    { title: 'Live.', message: 'Your post is available.' },
    { title: 'Posted.', message: 'Your article has been published.' },
    // Brand touch
    { title: 'Story released.', message: 'Your words are out in the world.', weight: 0.25 },
  ],
};

// ---------------------------------------------------------------------------
// Weighted random selection
// ---------------------------------------------------------------------------

/**
 * Pick a random phrase from a set, respecting weights.
 * Phrases without an explicit weight default to 1.
 * Brand-forward phrases (weight ~0.2) appear roughly 1 in 20–25 picks.
 */
export function getRandomPhrase(key: RoaryPhraseKey): RoaryPhrase {
  const phrases = ROARY_PHRASES[key];

  const totalWeight = phrases.reduce((sum, p) => sum + (p.weight ?? 1), 0);
  let roll = Math.random() * totalWeight;

  for (const phrase of phrases) {
    roll -= phrase.weight ?? 1;
    if (roll <= 0) return phrase;
  }

  // Fallback (shouldn't happen, but keeps TS happy)
  return phrases[0];
}
